import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";
import { apiClientJSON } from "@/apis/apiClient";
import { getUser } from "@/apis/user/user-apis";
import type { User } from "@/domain/user";
import { queryKeys } from "./keys";

const ENDPOINTS = {
  queryPrograms: "/backend/api/v1/admin/rewards/programs/query",
  queryRules: "/backend/api/v1/admin/rewards/rules/query",
  queryClaims: "/backend/api/v1/admin/rewards/claims/query",
  createProgram: "/backend/api/v1/admin/rewards/programs",
  updateProgram: (id: string | number) =>
    `/backend/api/v1/admin/rewards/programs/${id}`,
  createRule: "/backend/api/v1/admin/rewards/rules",
  updateRule: (id: string | number) =>
    `/backend/api/v1/admin/rewards/rules/${id}`,
};

export interface RewardProgram {
  id: string | number;
  name: string;
  status: string;
  organizationPaymentConfigurationId: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface RewardRule {
  id: string | number;
  name: string;
  rewardProgramId?: string | number;
  priority?: number;
  rewardConfig?: {
    fixed_amount_minor?: number;
  };
}

export interface RewardClaim {
  id: string;
  userId: string;
  legacyUserId?: number;
  triggerEventId?: string;
  rewardRuleId?: string | number;
  rewardAmountMinor: number;
  currency: string;
  status: string;
  claimedAt?: string;
}

interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
}

interface QueryParams {
  page?: number;
  size?: number;
}

interface RulesQueryParams {
  page?: number;
  size?: number;
  filter?: {
    reward_program_ids?: (string | number)[];
  };
}

const transformRule = (rule: Record<string, unknown>): RewardRule => ({
  id: rule.id as string | number,
  name: rule.name as string,
  rewardProgramId: rule.reward_program_id as string | number | undefined,
  priority: rule.priority as number | undefined,
  rewardConfig: rule.reward_config as RewardRule["rewardConfig"],
});

const transformUser = (claim: Record<string, unknown>): RewardClaim => ({
  id: claim.id as string,
  userId: claim.user_id as string,
  legacyUserId: claim.legacy_user_id as number | undefined,
  triggerEventId: claim.trigger_event_id as string | undefined,
  rewardRuleId: claim.reward_rule_id as string | number | undefined,
  rewardAmountMinor: claim.reward_amount_minor as number,
  currency: claim.currency as string,
  status: claim.status as string,
  claimedAt: claim.claimed_at as string | undefined,
});

const fetchRewardPrograms = async (
  params: QueryParams
): Promise<PaginatedResponse<RewardProgram>> => {
  const response = await apiClientJSON.post(ENDPOINTS.queryPrograms, params);
  const entities = response.data?.body?.query_response?.entities || [];
  return {
    content: entities,
    totalElements: entities.length,
    totalPages: 1,
  };
};

const fetchRewardRules = async (
  params: RulesQueryParams
): Promise<PaginatedResponse<RewardRule>> => {
  const response = await apiClientJSON.post(ENDPOINTS.queryRules, params);
  const entities = response.data?.body?.query_response?.entities || [];
  return {
    content: entities.map(transformRule),
    totalElements: entities.length,
    totalPages: 1,
  };
};

const fetchRewardClaims = async (
  params: QueryParams
): Promise<PaginatedResponse<RewardClaim>> => {
  const response = await apiClientJSON.post(ENDPOINTS.queryClaims, params);
  const entities = response.data?.body?.query_response?.entities || [];
  return {
    content: entities.map(transformUser),
    totalElements: entities.length,
    totalPages: 1,
  };
};

export function useRewardPrograms(params: QueryParams) {
  return useQuery({
    queryKey: queryKeys.rewards.programs(params),
    queryFn: () => fetchRewardPrograms(params),
    staleTime: 2 * 60 * 1000,
  });
}

export function useRewardClaims(params: QueryParams) {
  return useQuery({
    queryKey: queryKeys.rewards.claims(params),
    queryFn: () => fetchRewardClaims(params),
    staleTime: 2 * 60 * 1000,
  });
}

export function useRewardRules(
  programIds: (string | number)[],
  enabled = true
) {
  return useQuery({
    queryKey: queryKeys.rewards.rules({ programIds }),
    queryFn: () =>
      fetchRewardRules({ filter: { reward_program_ids: programIds } }),
    enabled: enabled && programIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch all reward rules without filtering (for claims tab)
 */
export function useAllRewardRules(enabled = true) {
  return useQuery({
    queryKey: queryKeys.rewards.rules({}),
    queryFn: () => fetchRewardRules({ page: 0, size: 1000 }),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch users for claims in parallel with TanStack Query caching.
 * Only works with legacy numeric IDs (claims without legacy IDs will show UUID).
 */
export function useClaimUsers(legacyUserIds: number[], enabled = true) {
  const uniqueIds = useMemo(
    () => Array.from(new Set(legacyUserIds.filter((id) => id != null))),
    [legacyUserIds]
  );

  const results = useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: queryKeys.users.detail(id),
      queryFn: () => getUser(id),
      enabled: enabled && id != null,
      staleTime: 5 * 60 * 1000,
      retry: 1,
    })),
  });

  const usersMap = useMemo(() => {
    const map = new Map<number, User>();
    results.forEach((result, index) => {
      if (result.data) {
        map.set(uniqueIds[index], result.data);
      }
    });
    return map;
  }, [results, uniqueIds]);

  const isLoading = results.some((r) => r.isLoading);
  const isFetching = results.some((r) => r.isFetching);

  return { usersMap, isLoading, isFetching };
}

export interface CreateRewardProgramData {
  name: string;
  description?: string;
  payment_configuration_id: string;
  is_active?: boolean;
  start_datetime?: string | null;
  end_datetime?: string | null;
}

export interface UpdateRewardProgramData {
  name?: string;
  description?: string;
  is_active?: boolean;
  start_datetime?: string | null;
  end_datetime?: string | null;
}

export interface CreateRewardRuleData {
  reward_program_id: string | number;
  name: string;
  trigger_type: string;
  reward_type: string;
  conditions?: Record<string, unknown>;
  reward_config?: Record<string, unknown>;
  display_title?: string | null;
  display_message?: string | null;
  priority?: number;
}

export interface UpdateRewardRuleData {
  name?: string;
  trigger_type?: string;
  conditions?: Record<string, unknown>;
  reward_config?: Record<string, unknown>;
  display_title?: string | null;
  display_message?: string | null;
  priority?: number;
  is_archived?: boolean;
}

const createRewardProgram = async (data: CreateRewardProgramData) => {
  const response = await apiClientJSON.post(ENDPOINTS.createProgram, data);
  return response.data?.body?.program;
};

const updateRewardProgram = async (
  id: string | number,
  data: UpdateRewardProgramData
) => {
  const response = await apiClientJSON.put(ENDPOINTS.updateProgram(id), {
    update: { set: data },
  });
  return response.data?.body?.program;
};

const createRewardRule = async (data: CreateRewardRuleData) => {
  const response = await apiClientJSON.post(ENDPOINTS.createRule, data);
  return response.data?.body?.rule;
};

const updateRewardRule = async (
  id: string | number,
  data: UpdateRewardRuleData
) => {
  const response = await apiClientJSON.put(ENDPOINTS.updateRule(id), {
    update: { set: data },
  });
  return response.data?.body?.rule;
};

export function useCreateRewardProgram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateRewardProgramData) => createRewardProgram(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rewards.all });
    },
  });
}

export function useUpdateRewardProgram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string | number;
      data: UpdateRewardProgramData;
    }) => updateRewardProgram(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rewards.all });
    },
  });
}

export function useCreateRewardRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateRewardRuleData) => createRewardRule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rewards.all });
    },
  });
}

export function useUpdateRewardRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string | number;
      data: UpdateRewardRuleData;
    }) => updateRewardRule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rewards.all });
    },
  });
}
